import { Box, Divider, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WizConnectForm from "./WizConnectForm";
import WizConfigPage from "./WizConfigPage";
import wiz_settings_0 from "./images/wiz_settings_0.png";
import wiz_settings_1 from "./images/wiz_settings_1.png";
import wiz_settings_2 from "./images/wiz_settings_2.png";
import wiz_settings_3 from "./images/wiz_settings_3.png";
import wiz_settings_4 from "./images/wiz_settings_4.png";

const WizIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("wiz");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerWizIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerWizIntegration(
      values.client_id,
      values.client_secret,
      values.api_endpoint_url
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Wiz integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? "Configure Wiz Integration"
            : "Once integrated, the Breeze platform will automatically read the configurations from Wiz and prioritize the suggested remediations."
        }
        image={logos.WizLogo}
        name={"wiz"}
        iconBorderRadius={0}
        iconWidth={60}
        iconHeight={30}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <>
              <WizConfigPage />
            </>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <Divider sx={{ my: 2 }} />
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Wiz">
                  <Typography variant={"body2"}>
                    Go to{" "}
                    <b>
                      Settings {">"} Access Management {">"} Service Accounts
                    </b>{" "}
                    page.
                  </Typography>
                  <Typography variant={"body2"}>
                    To create a Service Account, you must be logged in as a Wiz
                    user with <strong> Global Admin Role.</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Project-scoped roles can create Service Accounts only on
                    their own Projects.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Add Service Account">
                  <Typography variant={"body2"}>
                    1. Choose a meaningful name for the Service Account.
                  </Typography>
                  <Typography variant={"body2"}>
                    2. Choose{" "}
                    <strong> Custom Integration (GraphQL API).</strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: wiz_settings_0,
                        altext: "Wiz Add Service Account",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    3. Choose all Projects you want to integrate with Breeze.
                  </Typography>
                  <Typography variant={"body2"}>
                    4. Grant ALL <b>Read\List </b> scope permissions (check all
                    boxes in the section).
                  </Typography>
                  <ScreenshotAccordion
                    images={[{ file: wiz_settings_1, altext: "Wiz Scope" }]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    5. Click <b> Add Service Account </b> Button.
                  </Typography>
                  <Typography variant={"body2"}>
                    6. Copy the values and fill in the appropriate fields below.
                  </Typography>
                  <Typography variant={"body2"}>
                    <b>
                      The Client ID and Client Secret are displayed only once!
                    </b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      { file: wiz_settings_2, altext: "Wiz Credentials" },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Get the API endpoint URL">
                  <Typography variant={"body2"}>
                    At the top right of your Wiz portal, click the
                    <b> account icon {">"} Tenant info. </b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      { file: wiz_settings_3, altext: "Wiz Tenant info" },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Copy the API endpoint URL. and fill in the appropriate field
                    below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      { file: wiz_settings_4, altext: "Wiz API endpoint URL" },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Fill here the values">
                  <Box width={700}>
                    <WizConnectForm onSubmit={onSubmit} guideMode={guideMode} />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default WizIntegration;
